import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';
import { IUser } from '../../UserSlice';
import { RootState } from '../..';
import {
  adminCreateGroupTutorRateVersion,
  adminDeleteGroupTutorRateVersion,
  adminGetGroupTutorRateVersions,
  adminGetGroupTutorRateVersionsForDropdown,
  adminUpdateGroupTutorRateVersion,
  adminVerifyGroupTutorRateVersionDelete,
} from './adminApi';

export const GROUP_TUTOR_RATE_VERSION_CONDITION_TYPES = {
  TUTOR_REGISTRATION_DATE: 'TUTOR_REGISTRATION_DATE',
} as const;

export const GROUP_TUTOR_RATE_VERSION_TUTOR_REGISTRATION_DATE_CONDITIONS = {
  GREATER_THAN: 'GREATER_THAN',
  LESS_THAN: 'LESS_THAN',
  EQUAL: 'EQUAL',
  GREATER_THAN_OR_EQUAL: 'GREATER_THAN_OR_EQUAL',
  LESS_THAN_OR_EQUAL: 'LESS_THAN_OR_EQUAL',
} as const;

export type TGroupTutorRateVersionConditionTypes = typeof GROUP_TUTOR_RATE_VERSION_CONDITION_TYPES[keyof typeof GROUP_TUTOR_RATE_VERSION_CONDITION_TYPES];
export type TGroupTutorRateVersionTutorRegistrationDateConditionTypes = typeof GROUP_TUTOR_RATE_VERSION_TUTOR_REGISTRATION_DATE_CONDITIONS[keyof typeof GROUP_TUTOR_RATE_VERSION_TUTOR_REGISTRATION_DATE_CONDITIONS];

export interface IGroupTutorRateVersionTutorRegistrationDateCondition {
  _id: string;
  type: 'TUTOR_REGISTRATION_DATE';
  logic: TGroupTutorRateVersionTutorRegistrationDateConditionTypes;
  date: string;
}

export type TGroupTutorRateVersionCondition = IGroupTutorRateVersionTutorRegistrationDateCondition;

export interface IGroupTutorRateVersion {
  _id: string;
  name: string;
  conditions: TGroupTutorRateVersionCondition[];
  hideToUser: boolean;
  createdBy: string | IUser;
  updatedBy: string | IUser;
}

export interface IGroupTutorRateVersionDeleteVerificationData {
  groupTutorRateCount: number;
}

export interface IInitialGroupTutorRateVersionsState {
  status: 'idle' | 'loading' | 'rejected';
  // ADMIN
  adminGroupTutorRateVersionListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IGroupTutorRateVersion>;
  };
  adminGroupTutorRateVersionListDataForDropdown?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IGroupTutorRateVersion>;
  };
  adminGroupTutorRateVersionDeleteVerificationData?: IGroupTutorRateVersionDeleteVerificationData;
}

const initialState: IInitialGroupTutorRateVersionsState = {
  status: 'idle',
  // Admin
  adminGroupTutorRateVersionListData: undefined,
  adminGroupTutorRateVersionListDataForDropdown: undefined,
  adminGroupTutorRateVersionDeleteVerificationData: undefined,
};

export const GroupTutorRateVersionsSlice = createSlice({
  initialState,
  name: 'groupTutorRateVersions',
  reducers: {
    // Admin
    resetAdminGroupTutorRateVersionListData: (state) => {
      state.adminGroupTutorRateVersionListData = undefined;
    },
    resetAdminGroupTutorRateVersionListDataForDropdown: (state) => {
      state.adminGroupTutorRateVersionListDataForDropdown = undefined;
    },
    resetAdminGroupTutorRateVersionDeleteVerificationData: (state) => {
      state.adminGroupTutorRateVersionDeleteVerificationData = undefined;
    },
  },
  extraReducers(builder) {
    // Admin
    builder
      .addCase(adminGetGroupTutorRateVersions.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminGroupTutorRateVersionListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminGroupTutorRateVersionListData = newAdminGroupTutorRateVersionListData;
      })
      .addCase(
        adminGetGroupTutorRateVersionsForDropdown.fulfilled,
        (state, action) => {
          const result = action.payload;

          const newAdminGroupTutorRateVersionListDataForDropdown = {
            limit: result.limit,
            offset: result.offset,
            totalDocs: result.totalDocs || 0,
            list: result.docs || [],
          };

          state.adminGroupTutorRateVersionListDataForDropdown = newAdminGroupTutorRateVersionListDataForDropdown;
        }
      )
      .addCase(adminCreateGroupTutorRateVersion.fulfilled, (state, action) => {
        const result = action.payload;

        if (state.adminGroupTutorRateVersionListData) {
          const newAdminGroupTutorRateVersionListData = {
            ...state.adminGroupTutorRateVersionListData,
          };

          newAdminGroupTutorRateVersionListData.list.unshift(result);

          if (newAdminGroupTutorRateVersionListData.totalDocs) {
            newAdminGroupTutorRateVersionListData.totalDocs += 1;
          } else {
            newAdminGroupTutorRateVersionListData.totalDocs = 1;
          }

          state.adminGroupTutorRateVersionListData = newAdminGroupTutorRateVersionListData;
        }
      })
      .addCase(adminDeleteGroupTutorRateVersion.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminGroupTutorRateVersionListData &&
          state.adminGroupTutorRateVersionListData.list &&
          state.adminGroupTutorRateVersionListData.list.length
        ) {
          const newAdminGroupTutorRateVersionListData = {
            ...state.adminGroupTutorRateVersionListData,
          };

          newAdminGroupTutorRateVersionListData.totalDocs = newAdminGroupTutorRateVersionListData.totalDocs
            ? newAdminGroupTutorRateVersionListData.totalDocs - 1
            : 0;

          newAdminGroupTutorRateVersionListData.list = newAdminGroupTutorRateVersionListData.list.filter(
            (item) => item._id !== result._id
          );

          state.adminGroupTutorRateVersionListData = newAdminGroupTutorRateVersionListData;
        }
      })
      .addCase(adminUpdateGroupTutorRateVersion.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminGroupTutorRateVersionListData &&
          state.adminGroupTutorRateVersionListData.list &&
          state.adminGroupTutorRateVersionListData.list.length
        ) {
          const newAdminGroupTutorRateVersionListData = {
            ...state.adminGroupTutorRateVersionListData,
          };

          const index = newAdminGroupTutorRateVersionListData.list.findIndex(
            (item) => item._id === result._id
          );

          if (index > -1) {
            newAdminGroupTutorRateVersionListData.list[index] = result;
          }

          state.adminGroupTutorRateVersionListData = newAdminGroupTutorRateVersionListData;
        }
      })
      .addCase(
        adminVerifyGroupTutorRateVersionDelete.fulfilled,
        (state, action) => {
          const result = action.payload;

          state.adminGroupTutorRateVersionDeleteVerificationData = result;
        }
      );

    // Common Matchers
    builder.addMatcher(
      isFulfilled(
        // Admin
        adminCreateGroupTutorRateVersion,
        adminGetGroupTutorRateVersions,
        adminDeleteGroupTutorRateVersion,
        adminUpdateGroupTutorRateVersion,
        adminVerifyGroupTutorRateVersionDelete
      ),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isPending(
        // Admin
        adminCreateGroupTutorRateVersion,
        adminGetGroupTutorRateVersions,
        adminDeleteGroupTutorRateVersion,
        adminUpdateGroupTutorRateVersion,
        adminVerifyGroupTutorRateVersionDelete
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isRejected(
        // Admin
        adminCreateGroupTutorRateVersion,
        adminGetGroupTutorRateVersions,
        adminDeleteGroupTutorRateVersion,
        adminUpdateGroupTutorRateVersion,
        adminVerifyGroupTutorRateVersionDelete
      ),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const getGroupTutorRateVersionsState = (
  state: RootState
): RootState['groupTutorRateVersions'] => state.groupTutorRateVersions;

export const {
  resetAdminGroupTutorRateVersionListData,
  resetAdminGroupTutorRateVersionListDataForDropdown,
  resetAdminGroupTutorRateVersionDeleteVerificationData,
} = GroupTutorRateVersionsSlice.actions;

export * from './adminApi';
export default GroupTutorRateVersionsSlice.reducer;
